import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DealDetails = ({ deal, onClose, onEdit }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString)?.toLocaleDateString('pt-BR');
  };

  const handleEdit = () => {
    if (onEdit && deal) {
      onEdit(deal);
    }
  };

  if (!deal) {
    return (
      <div className="h-full bg-card flex flex-col items-center justify-center p-8 text-center">
        <div className="w-20 h-20 rounded-full bg-muted/50 flex items-center justify-center mb-4">
          <Icon name="TrendingUp" size={40} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Selecione uma Negociação
        </h3>
        <p className="text-sm text-muted-foreground max-w-xs">
          Escolha uma negociação da lista para ver os detalhes completos
        </p>
      </div>
    );
  }

  // Calculate revenue and profit from deal values
  const dealValue = parseFloat(deal?.value) || 0;
  const supplierCost = parseFloat(deal?.commission) || 0;
  const revenue = dealValue - supplierCost;
  const profitWithoutDiscount = revenue;
  const profitWith6Discount = revenue * 0.94;

  return (
    <div className="h-full bg-card flex flex-col">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="FileText" size={20} />
          Detalhes da Negociação
        </h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-muted rounded-md transition-colors"
        >
          <Icon name="X" size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Product Info */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Produto
          </h3>
          <div className="bg-muted/30 rounded-lg p-4">
            <p className="text-lg font-semibold text-foreground mb-2">
              {deal?.title || 'Sem título'}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Valor do Produto</span>
              <span className="text-lg font-bold text-primary">
                {formatCurrency(dealValue)}
              </span>
            </div>
          </div>
        </div>

        {/* Financial Summary */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Resumo Financeiro
          </h3>
          <div className="space-y-3">
            <div className="bg-muted/30 rounded-lg p-4 flex items-center justify-between">
              <span className="text-sm text-foreground">Custo do Fornecedor</span>
              <span className="text-sm font-semibold text-error">
                {formatCurrency(supplierCost)}
              </span>
            </div>
            <div className="bg-primary/10 rounded-lg p-4 flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">Receita</span>
              <span className="text-lg font-bold text-primary">
                {formatCurrency(revenue)}
              </span>
            </div>
            <div className="bg-success/10 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-foreground">Lucro (sem redução)</span>
                <span className="text-sm font-semibold text-success">
                  {formatCurrency(profitWithoutDiscount)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">Lucro (com 6% redução)</span>
                <span className="text-lg font-bold text-success">
                  {formatCurrency(profitWith6Discount)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Client Info */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
            <Icon name="User" size={14} />
            Cliente
          </h3>
          <div className="bg-muted/30 rounded-lg p-4 space-y-2">
            <div>
              <p className="text-sm font-semibold text-foreground">{deal?.clientName}</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon name="Mail" size={12} />
              <span>{deal?.clientEmail}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon name="Phone" size={12} />
              <span>{deal?.clientPhone}</span>
            </div>
            {deal?.isNewClient && (
              <div className="mt-2 px-2 py-1 bg-primary/10 rounded text-xs text-primary font-medium inline-flex items-center gap-1">
                <Icon name="Info" size={12} />
                Novo Cliente
              </div>
            )}
          </div>
        </div>

        {/* Supplier Info */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
            <Icon name="Truck" size={14} />
            Fornecedor
          </h3>
          <div className="bg-muted/30 rounded-lg p-4 space-y-2">
            <div>
              <p className="text-sm font-semibold text-foreground">{deal?.supplierName}</p>
            </div>
            {deal?.supplierEmail && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon name="Mail" size={12} />
                <span>{deal?.supplierEmail}</span>
              </div>
            )}
            {deal?.supplierPhone && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon name="Phone" size={12} />
                <span>{deal?.supplierPhone}</span>
              </div>
            )}
            {deal?.isNewSupplier && (
              <div className="mt-2 px-2 py-1 bg-primary/10 rounded text-xs text-primary font-medium inline-flex items-center gap-1">
                <Icon name="Info" size={12} />
                Novo Fornecedor
              </div>
            )}
          </div>
        </div>

        {/* Dates */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
            <Icon name="Calendar" size={14} />
            Datas
          </h3>
          <div className="bg-muted/30 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Data da Venda</span>
              <span className="text-sm font-semibold text-foreground">
                {formatDate(deal?.saleDate)}
              </span>
            </div>
            {deal?.invoiceReceiptDate && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">Recebimento da Fatura</span>
                <span className="text-sm font-semibold text-foreground">
                  {formatDate(deal?.invoiceReceiptDate)}
                </span>
              </div>
            )}
            {deal?.fiscalNoteIssueDate && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">Emissão da Nota Fiscal</span>
                <span className="text-sm font-semibold text-foreground">
                  {formatDate(deal?.fiscalNoteIssueDate)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Receivables */}
        {deal?.amountReceivable && (
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
              <Icon name="DollarSign" size={14} />
              Valor a Receber
            </h3>
            <div className="bg-primary/10 rounded-lg p-4">
              <p className="text-2xl font-bold text-primary">
                {formatCurrency(deal?.amountReceivable)}
              </p>
            </div>
          </div>
        )}

        {/* Documents */}
        {(deal?.invoiceFileName || deal?.fiscalNoteFileName) && (
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
              <Icon name="FileText" size={14} />
              Documentos
            </h3>
            <div className="space-y-2">
              {deal?.invoiceFileName && (
                <div className="bg-muted/30 rounded-lg p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="FileText" size={20} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {deal?.invoiceFileName}
                    </p>
                    <p className="text-xs text-muted-foreground">Fatura</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Download"
                    onClick={() => console.log('Download invoice')}
                  />
                </div>
              )}
              {deal?.fiscalNoteFileName && (
                <div className="bg-muted/30 rounded-lg p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded bg-success/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="FileText" size={20} className="text-success" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {deal?.fiscalNoteFileName}
                    </p>
                    <p className="text-xs text-muted-foreground">Nota Fiscal</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Download"
                    onClick={() => console.log('Download fiscal note')}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="p-4 border-t border-border">
        <Button
          variant="default"
          className="w-full"
          iconName="Edit"
          iconPosition="left"
          onClick={handleEdit}
        >
          Editar Negociação
        </Button>
      </div>
    </div>
  );
};

export default DealDetails;